var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "@/context/ApiClientContext";
import { getFirstParagraph, hasMultipleParagraphs, } from "@/utils/textProcessing";
import DOMPurify from "dompurify";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { FaShoppingCart, FaTimes } from "react-icons/fa";
import { useCart } from "@/context/CartContext";
import "./ProductDetailsPage.css";
const ProductDetailsPage = () => {
    var _a, _b, _c;
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [selectedVariant, setSelectedVariant] = useState(0);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { cart, addToCart, removeLineItem, isInCart } = useCart();
    const inCart = product ? isInCart(product.id, selectedVariant + 1) : false;
    useEffect(() => {
        const fetchProduct = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!id) {
                setError("Product ID is missing");
                setLoading(false);
                return;
            }
            try {
                const data = yield apiClient.getProduct(id);
                if (!data)
                    throw new Error("Product not found");
                setProduct(data);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load product");
            }
            finally {
                setLoading(false);
            }
        });
        fetchProduct();
    }, [id, apiClient]);
    const handleNextImage = () => {
        if (product) {
            setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
        }
    };
    const handlePrevImage = () => {
        if (product) {
            setCurrentImageIndex((prev) => prev === 0 ? product.images.length - 1 : prev - 1);
        }
    };
    const openImageModal = (index) => {
        setCurrentImageIndex(index);
        setIsModalOpen(true);
        document.body.style.overflow = "hidden";
    };
    const closeImageModal = () => {
        setIsModalOpen(false);
        document.body.style.overflow = "auto";
    };
    const handleRemoveFromCart = () => {
        if (!cart || !product)
            return;
        const lineItem = cart.lineItems.find((item) => item.productId === product.id &&
            item.variant.id === selectedVariant + 1);
        if (lineItem) {
            removeLineItem(lineItem.id);
        }
        else {
            console.warn("Line item not found for removal!", {
                productId: product.id,
                selectedVariant,
                lineItems: cart.lineItems.map((i) => ({
                    id: i.id,
                    productId: i.productId,
                    variantId: i.variant.id,
                })),
            });
        }
    };
    if (loading)
        return _jsx("div", { className: "main-content", children: "Loading..." });
    if (error || !product) {
        return (_jsxs("div", { className: "main-content", children: [_jsx("p", { children: error || "Product not found" }), _jsx("button", { onClick: () => navigate(-1), className: "back-button", children: "\u2190 Back to Shop" })] }));
    }
    const sanitizedDesc = DOMPurify.sanitize((_a = product.description) !== null && _a !== void 0 ? _a : "");
    return (_jsxs("div", { className: "main-content", children: [_jsxs("div", { className: "product-detail-container", children: [_jsxs("div", { className: "product-gallery", children: [_jsxs("div", { className: "slider-container", children: [product.images.length > 1 && (_jsxs(_Fragment, { children: [_jsx("button", { onClick: handlePrevImage, className: "slider-arrow left-arrow", children: _jsx(FiChevronLeft, { size: 24 }) }), _jsx("button", { onClick: handleNextImage, className: "slider-arrow right-arrow", children: _jsx(FiChevronRight, { size: 24 }) })] })), _jsx("div", { className: "main-image", onClick: () => openImageModal(currentImageIndex), children: _jsx("img", { src: (_b = product.images[currentImageIndex]) === null || _b === void 0 ? void 0 : _b.url, alt: product.name }) })] }), _jsx("div", { className: "thumbnail-container", children: product.images.map((img, idx) => (_jsx("div", { className: `thumbnail ${currentImageIndex === idx ? "active" : ""}`, onClick: () => setCurrentImageIndex(idx), children: _jsx("img", { src: img.url, alt: `Thumb ${idx}` }) }, idx))) })] }), _jsxs("div", { className: "product-info", children: [_jsx("h2", { className: "product-title", children: product.name }), product.variants.length > 0 && (_jsxs("div", { className: "variants-section", children: [_jsx("h3", { children: "Variants" }), _jsx("div", { className: "variant-thumbnails", children: product.variants.map((variant, idx) => {
                                            var _a;
                                            return (_jsx("div", { className: `variant-thumbnail ${selectedVariant === idx ? "active" : ""}`, onClick: () => setSelectedVariant(idx), children: ((_a = variant.images) === null || _a === void 0 ? void 0 : _a[0]) && (_jsx("img", { src: variant.images[0].url, alt: `Variant ${idx}` })) }, idx));
                                        }) })] })), _jsxs("div", { className: "product-description", children: [_jsx("h3", { children: "Description" }), _jsx("div", { className: "description-content", dangerouslySetInnerHTML: {
                                            __html: showFullDescription
                                                ? sanitizedDesc
                                                : getFirstParagraph(sanitizedDesc),
                                        } }), hasMultipleParagraphs(sanitizedDesc) && (_jsx("button", { className: "show-more-btn", onClick: () => setShowFullDescription((v) => !v), children: showFullDescription ? "Show less" : "Show more" }))] }), _jsx("div", { className: "product-price-container", children: product.priceDiscounted ? (_jsxs("div", { className: "price-with-discount", children: [_jsxs("span", { className: "price-discounted", children: [product.priceDiscounted, " \u20AC"] }), _jsxs("span", { className: "price-original", children: [product.price, " \u20AC"] })] })) : (_jsxs("span", { className: "price-regular", children: [product.price, " \u20AC"] })) }), _jsx("div", { className: "buy-section", children: inCart ? (_jsxs("button", { className: "button__removeFromCart", onClick: handleRemoveFromCart, children: [_jsx(FaTimes, {}), " REMOVE FROM CART"] })) : (_jsxs("button", { className: "button__addToCart", onClick: () => addToCart(product.id, selectedVariant + 1), children: [_jsx(FaShoppingCart, {}), " ADD TO CART"] })) })] })] }), isModalOpen && (_jsx("div", { className: "image-modal", onClick: closeImageModal, children: _jsxs("div", { className: "modal-content", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { className: "close-modal", onClick: closeImageModal, children: _jsx(FiX, { size: 24 }) }), _jsx("img", { src: (_c = product.images[currentImageIndex]) === null || _c === void 0 ? void 0 : _c.url, alt: "Modal View" }), product.images.length > 1 && (_jsxs("div", { className: "modal-image-counter", children: [currentImageIndex + 1, " / ", product.images.length] }))] }) }))] }));
};
export default ProductDetailsPage;
